import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import { ATOMCAMP_URL, BOOK_DEMO_HREF, CONTACT_MAIL } from "@/lib/marketing-links";

type FooterLink = { label: string; href: string; external?: boolean };

/**
 * The same sitemap as the navbar's menus (components/landing-page/layout/Navbar.tsx): when a
 * menu item is added, renamed or hidden there, mirror it here.
 */
const NAV_COLUMNS: readonly { title: string; links: readonly FooterLink[] }[] = [
    {
        title: "Product",
        links: [
            { label: "Product", href: "/product" },
            { label: "How It Works", href: "/how-it-works" },
            { label: "FAQs", href: "/product#faq" },
        ],
    },
    {
        title: "Industries",
        links: [
            { label: "Public Sector", href: "/industries#public-sector" },
            { label: "Education", href: "/industries#education" },
            { label: "Manufacturing", href: "/industries#manufacturing" },
            { label: "Banking & Finance", href: "/industries#banking" },
        ],
    },
    {
        title: "About Us",
        links: [
            { label: "Mission", href: "/about#mission" },
            { label: "Our Story", href: "/about#our-story" },
            { label: "Leadership", href: "/about#leadership" },
            { label: "Global Footprint", href: "/about#global-footprint" },
            { label: "atomcamp", href: ATOMCAMP_URL, external: true },
        ],
    },
];

const linkClassName =
    "text-mkt-on-surface-variant hover:text-mkt-primary transition-transform inline-flex items-center gap-1 hover:translate-x-1";

const LEGAL_LINKS = [
    { label: "Terms of Service", href: "/terms-of-service" },
    { label: "Privacy Policy", href: "/privacy-policy" },
    { label: "Cookie Policy", href: "/cookie-policy" },
] as const;

export default function Footer() {
    return (
        <footer className="w-full px-margin-mobile md:px-margin-desktop py-size-lg bg-mkt-surface-container-lowest border-t border-mkt-outline-variant/50">
            {/* Phones: brand and contact span the width, the two link lists sit side by side. */}
            <div className="grid grid-cols-2 gap-size-lg md:flex md:flex-row md:justify-between">
            <div className="col-span-2 space-y-size-md max-w-xs">
                <Image
                    alt="InaraX Logo"
                    className="w-32 object-contain -mb-3"
                    src="/images/logo.png"
                    width={128}
                    height={40}
                />
                <p className="text-mkt-on-surface-variant font-body-md">
                    The enterprise AI learning platform.
                </p>
                <div className="flex gap-size-md">
                    <a
                        href="https://www.linkedin.com/company/inarax/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="LinkedIn"
                        className="text-mkt-on-surface-variant hover:text-mkt-primary transition-colors"
                    >
                        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                            <rect x="2" y="9" width="4" height="12" />
                            <circle cx="4" cy="4" r="2" />
                        </svg>
                    </a>
                    <a
                        href="https://www.instagram.com/inarax.ai/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="text-mkt-on-surface-variant hover:text-mkt-primary transition-colors"
                    >
                        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                        </svg>
                    </a>
                    {/* No Facebook page URL yet — placeholder link until one exists. */}
                    <a
                        href="https://www.facebook.com/people/InaraX/61587219626478/"
                        aria-label="Facebook"
                        className="text-mkt-on-surface-variant hover:text-mkt-primary transition-colors"
                    >
                        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                        </svg>
                    </a>
                </div>
            </div>

            {NAV_COLUMNS.map((column) => (
                <div key={column.title}>
                    <h4 className="font-headline-md text-headline-md mb-size-md">{column.title}</h4>
                    <ul className="space-y-size-sm list-none pl-0 ml-0">
                        {column.links.map((link) => (
                            <li key={link.label}>
                                {link.external ? (
                                    <a className={linkClassName} href={link.href} target="_blank" rel="noopener noreferrer">
                                        {link.label}
                                        <ArrowUpRight aria-hidden="true" className="h-4 w-4" />
                                    </a>
                                ) : (
                                    <Link className={linkClassName} href={link.href}>
                                        {link.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>
                    {/* Individual learners are not part of the main journey; this is their way in. */}
                    {column.title === "Product" ? (
                        <Link
                            href="/catalog"
                            className="mt-size-md inline-block font-label-md text-label-md text-mkt-on-surface-variant/80 underline underline-offset-4 hover:text-mkt-primary transition-colors"
                        >
                            Individual learning
                        </Link>
                    ) : null}
                </div>
            ))}

            <div>
                <h4 className="font-headline-md text-headline-md mb-size-md">Legal</h4>
                <ul className="space-y-size-sm list-none pl-0 ml-0">
                    {LEGAL_LINKS.map((link) => (
                        <li key={link.label}>
                            <Link className={linkClassName} href={link.href}>
                                {link.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="col-span-2 max-w-xs">
                <h4 className="font-headline-md text-headline-md mb-size-md">Contact</h4>
                <p className="text-mkt-on-surface-variant font-body-md">
                    Ready to build AI capability across your workforce? Talk to our team.
                </p>
                <div className="mt-size-md flex flex-col items-start gap-size-sm">
                    <Link
                        className="text-mkt-primary font-label-md text-label-md border-b-2 border-mkt-primary"
                        href={BOOK_DEMO_HREF}
                    >
                        Book a Consultation
                    </Link>
                    <a
                        className="text-mkt-on-surface-variant font-label-md text-label-md hover:text-mkt-primary transition-colors"
                        href={`mailto:${CONTACT_MAIL}`}
                    >
                        {CONTACT_MAIL}
                    </a>
                </div>
            </div>
            </div>

            <div className="pt-size-lg mt-size-lg border-t border-mkt-outline-variant/30 text-center">
                <p className="text-mkt-on-surface-variant font-label-md text-label-md">
                    &copy; 2026 InaraX AI. All rights reserved.
                </p>
            </div>
        </footer>
    );
}
